/**
 * Copyright (c) 2022 Yoshinobu Obata
 * Released under the Apache-2.0 license
 * LICENSE: http://www.apache.org/licenses/LICENSE-2.0
 */
const ArgumentType = require("../../extension-support/argument-type");
const Cast = require("../../util/cast");
const BlockType = require("../../extension-support/block-type");
const formatMessage = require("format-message");
const log = require('../../util/log');

const { Message, AvailableLocales } = require('./config');
const TranslationModule = require('./translation');
const {
    getSharedModule,
    hasSharedModule,
    isTransformersLoaded,
    isTransformersLoading,
    setTransformersState,
    waitForTransformers
} = require('./shared');

// 共有翻訳モジュールの名前
const TRANSLATION_MODULE_NAME = 'translationModule';

class kurikitTranslationBlocks {
    constructor(runtime) {
        this.runtime = runtime;
        
        // 共有翻訳モジュールをチェック、なければ作成
        if (hasSharedModule(TRANSLATION_MODULE_NAME)) {
            this.translation = getSharedModule(TRANSLATION_MODULE_NAME);
            log.log('Using existing shared translation module');
        } else {
            this.translation = new TranslationModule();
            log.log('Created new translation module');
        }
        
        // Transformers.jsをロードし、翻訳モデルを初期化
        this._initializeExtension();
    }

    async _initializeExtension() {
        try {
            // Transformers.jsをロード
            await this._loadTransformersJS();
            
            // 翻訳モデルが既にロード済みならスキップ
            const status = this.translation.getStatus();
            if (status === 'ready') {
                log.log('Translation model already loaded (shared)');
                return;
            }
            
            if (status === 'loading') {
                log.log('Translation model is loading (shared), waiting...');
                return;
            }
            
            // 翻訳モデルを事前にロード
            log.log('Pre-loading translation model...');
            await this.translation.initialize();
            log.log('Translation model pre-loaded successfully');
        } catch (error) {
            log.error('Failed to initialize extension:', error);
        }
    }

    async _loadTransformersJS() {
        try {
            // 既にロード済みの場合はスキップ
            if (isTransformersLoaded()) {
                log.log('Transformers.js already loaded');
                return;
            }

            // 他の拡張機能がロード中の場合は待機
            if (isTransformersLoading()) {
                log.log('Waiting for Transformers.js to be loaded by another extension...');
                await waitForTransformers();
                return;
            }

            // ロード開始を宣言
            setTransformersState('loading');

            const script = document.createElement('script');
            script.type = 'module';
            script.textContent = `
                import { pipeline, env } from 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.0.2';
                env.allowLocalModels = false;
                env.useBrowserCache = false;
                window.transformers = { pipeline, env };
            `;
            document.head.appendChild(script);

            await waitForTransformers();

            // ロード完了を宣言
            setTransformersState('loaded');

            log.log('Transformers.js loaded successfully');
        } catch (error) {
            log.error('Failed to load Transformers.js:', error);
        }
    }

    setLocale() {
        let locale = formatMessage.setup().locale;
        if (AvailableLocales.includes(locale)) {
            return locale;
        } else {
            return 'en';
        }
    }

    getInfo() {
        const locale = this.setLocale();
        return {
            id: "kurikittranslation",
            name: Message.categoryName[locale],
            blocks: [
                {
                    opcode: "translate",
                    text: Message.translate[locale],
                    blockType: BlockType.REPORTER,
                    arguments: {
                        WORDS: {
                            type: ArgumentType.STRING,
                            defaultValue: "こんにちは、いい天気ですね"
                        },
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languageMenu',
                            defaultValue: 'english'
                        }
                    },
                },
                "---",
                {
                    opcode: "modelStatus",
                    text: Message.modelStatus[locale],
                    blockType: BlockType.REPORTER,
                },
            ],
            menus: {
                languageMenu: {
                    acceptReporters: false,
                    items: [
                        { text: Message.languageMenu.english[locale], value: 'english' },
                        { text: Message.languageMenu.japanese[locale], value: 'japanese' },
                        { text: Message.languageMenu.chinese[locale], value: 'chinese' },
                        { text: Message.languageMenu.korean[locale], value: 'korean' },
                        { text: Message.languageMenu.french[locale], value: 'french' },
                        { text: Message.languageMenu.spanish[locale], value: 'spanish' },
                        { text: Message.languageMenu.german[locale], value: 'german' }
                    ]
                }
            }
        };
    }

    modelStatus() {
        const status = this.translation.getStatus();
        const locale = this.setLocale();
        
        switch(status) {
            case 'loading':
                return Message.modelLoading[locale];
            case 'ready':
                return Message.modelReady[locale];
            case 'error':
                return Message.modelError[locale];
            default:
                return Message.modelNotReady[locale];
        }
    }

    async translate(args) {
        const text = Cast.toString(args.WORDS);
        const targetLang = Cast.toString(args.LANGUAGE);
        
        if (!text || text.trim() === '') {
            return '';
        }

        if (this.translation.getStatus() === 'loading') {
            return Message.loadingMessage[this.setLocale()];
        }

        return await this.translation.translate(text, targetLang);
    }
}

module.exports = kurikitTranslationBlocks;